// « Turn on notifications » — push alerts on this device (2026-10-01).
//
// iPhone: Web Push exists only inside the installed app (iOS 16.4+), and the
// permission prompt only appears when asked for inside a tap — so the button's
// handler calls Notification.requestPermission() before anything else
// (lib/pwa/push.ts turnOnPush). In a Safari tab the card says to install first.
//
// ⚠️ NEVER FOR NOBODY (owner, 2026-10-01: « one time I was logged out and I
// still got a notification »): logged out, the card renders nothing and runs
// nothing — the gate below keeps every hook of the real card from mounting.
// Logging out also unsubscribes the device (store/useAuthStore.ts signOut).
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { pushApi, type PushTestResult } from '@/lib/api';
import { useAuthStore } from '@/store/useAuthStore';
import { isIosDevice, isStandalone, isPushSupported, pushPlatform } from '@/lib/pwa/detect';
import { pushCardState } from '@/lib/pwa/cardState';
import { currentSubscription, saveSubscription, turnOnPush, type SubLike } from '@/lib/pwa/push';
import { PhoneCard, BellIcon } from './phoneAppUi';

/** The endpoint already re-sent to the server during this page load. */
let resyncedEndpoint: string | null = null;

/** `hideWhenOn`: the dashboard shows the card only until notifications are on for this
 *  device; the tests stay on the Notifications page. The silent re-sync still runs. */
export function PushNotificationsCard({ hideWhenOn = false }: { hideWhenOn?: boolean } = {}) {
  const user = useAuthStore((s) => s.user);
  if (!user) return null;
  return <PushCard hideWhenOn={hideWhenOn} />;
}

function PushCard({ hideWhenOn }: { hideWhenOn: boolean }) {
  const ios = isIosDevice(navigator);
  const standalone = isStandalone(window);
  const supported = isPushSupported(window);
  const platform = pushPlatform(navigator);
  const canCheck = supported && !(ios && !standalone);

  const [permission, setPermission] = useState<NotificationPermission>(() => (supported ? Notification.permission : 'default'));
  const [sub, setSub] = useState<SubLike | null>(null);
  const [checked, setChecked] = useState(!canCheck);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState<'test' | 'wa_line' | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Is this device subscribed already? If so, tell the server again once per
  // page load — it may have lost the row (a 410 clean-up, a reset), and the
  // worker cannot re-register a device by itself.
  useEffect(() => {
    if (!canCheck) return;
    let alive = true;
    currentSubscription(navigator.serviceWorker)
      .then((s) => {
        if (!alive) return;
        setSub(s);
        if (s && Notification.permission === 'granted' && resyncedEndpoint !== s.endpoint) {
          resyncedEndpoint = s.endpoint;
          saveSubscription(s, pushApi, platform).catch(() => { resyncedEndpoint = null; });
        }
      })
      .catch(() => { /* no registration yet: shown as off */ })
      .finally(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [canCheck, platform]);

  const state = pushCardState({ ios, standalone, supported, permission, subscribed: !!sub }, { busy, error });
  // A split second while the browser answers: show nothing rather than a
  // « Turn on » that flips to « on ».
  if (!checked) return null;

  const turnOn = () => {
    setBusy(true);
    setError(null);
    setNote(null);
    // ⚠️ NO AWAIT BEFORE THIS CALL — it asks for permission inside the tap.
    void turnOnPush({
      notification: Notification,
      serviceWorker: navigator.serviceWorker,
      api: pushApi,
      platform,
      isSignedIn: () => !!useAuthStore.getState().user,
    })
      .then(async (r) => {
        setPermission(Notification.permission);
        if (r.status === 'on') setSub(await currentSubscription(navigator.serviceWorker));
        else if (r.status === 'error') setError(r.message);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  };

  const runTest = async (kind: 'test' | 'wa_line') => {
    setTesting(kind);
    setNote(null);
    // The server lost this device (none registered, or the push service said it is gone):
    // register this one again and say so.
    const registerAgain = async () => {
      if (!sub) return false;
      await saveSubscription(sub, pushApi, platform);
      setNote('This device wasn’t registered on the server — it is now. Send the test again.');
      return true;
    };
    try {
      let r: PushTestResult | undefined;
      try {
        r = await pushApi.test(kind);
      } catch (e) {
        if ((e as { status?: number } | null)?.status === 404 && await registerAgain()) return;
        throw e;
      }
      if (r?.pushed === undefined || r.pushed > 0) {
        setNote(kind === 'wa_line'
          ? 'Test WhatsApp-down alert sent (marked TEST). It should arrive within a few seconds.'
          : 'Test sent — it should arrive within a few seconds.');
      } else if ((r.gone ?? 0) > 0 && await registerAgain()) {
        // registered again — the note says so
      } else {
        setNote(`The push service refused the test: ${r.errors?.[0] ?? r.error ?? 'no reason given'}.`);
      }
    } catch (e) {
      setNote(`Couldn’t send the test: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setTesting(null);
    }
  };

  const title = 'Notifications';
  switch (state) {
    case 'needs-install':
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p>Install the app first, then open it from the icon. On iPhone, alerts only work inside the installed app.</p>
          {ios && <IphoneInstallGuide />}
        </PhoneCard>
      );
    case 'unsupported':
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p>Notifications are not supported in this browser.</p>
        </PhoneCard>
      );
    case 'blocked':
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p>
            Notifications are blocked on this device.{' '}
            {ios
              ? 'To allow them: Settings → Notifications → Storiz.'
              : 'Allow them in the site settings (the icon left of the address bar), then reload.'}
          </p>
        </PhoneCard>
      );
    case 'on':
      if (hideWhenOn) return null;
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p>Notifications are on for this device. Chats you take over notify you here — only you, only your chats.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" loading={testing === 'test'} disabled={testing !== null} onClick={() => { void runTest('test'); }}>
              Send a test
            </Button>
            <Button size="sm" variant="secondary" loading={testing === 'wa_line'} disabled={testing !== null} onClick={() => { void runTest('wa_line'); }}>
              Test WhatsApp-down alert
            </Button>
          </div>
          {note && <p className="mt-2 text-gray-700" role="status">{note}</p>}
        </PhoneCard>
      );
    case 'error':
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p className="text-error" role="alert">{error}</p>
          <div className="mt-2">
            <Button size="sm" onClick={turnOn}>Try again</Button>
          </div>
        </PhoneCard>
      );
    default: // 'off' | 'working'
      return (
        <PhoneCard icon={<BellIcon />} title={title}>
          <p>Get alerts on this device: orders that need you, the WhatsApp line going down, and clients who write in a chat you took over.</p>
          <div className="mt-2">
            <Button size="sm" loading={state === 'working'} onClick={turnOn}>
              {state === 'working' ? 'Turning on…' : 'Turn on notifications'}
            </Button>
          </div>
        </PhoneCard>
      );
  }
}

/**
 * iPhone: Web Push exists only in the app added to the home screen, iOS 16.4
 * or later (owner, 2026-10-19: « show a short guide for that »).
 */
function IphoneInstallGuide() {
  return (
    <ol className="mt-2 list-decimal space-y-0.5 pl-5" data-testid="ios-install-guide">
      <li>Open app.storiz.ma/admin in <b>Safari</b> (iOS 16.4 or later).</li>
      <li>Tap <b>Share</b> (the square with the arrow), then <b>Add to Home Screen</b> (« Sur l’écran d’accueil »).</li>
      <li>Open Storiz from the new icon on your home screen, sign in.</li>
      <li>Come back here and tap <b>Turn on notifications</b>, then <b>Allow</b>.</li>
    </ol>
  );
}
